
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateContact, useUpdateContact, Contact, ContactFormData } from '@/hooks/useContacts';
import { useBrands } from '@/hooks/useBrands';
import { useCampaigns } from '@/hooks/useCampaigns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';

const contactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  phone_number: z.string().optional(),
  company_name: z.string().optional(),
  designation: z.string().optional(),
  contact_type: z.enum(['Marketing', 'Finance', 'Legal', 'Logistics', 'Vendor', 'Other']),
  linked_brand_ids: z.array(z.string()).optional(),
  linked_campaign_ids: z.array(z.string()).optional(),
});

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ contact, onSuccess }) => {
  const { data: brands = [] } = useBrands();
  const { data: campaigns = [] } = useCampaigns();
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: contact?.name || '',
      email: contact?.email || '',
      phone_number: contact?.phone_number || '',
      company_name: contact?.company_name || '',
      designation: contact?.designation || '',
      contact_type: contact?.contact_type || 'Other',
      linked_brand_ids: contact?.linked_brand_ids || [],
      linked_campaign_ids: contact?.linked_campaign_ids || [],
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      if (contact) {
        await updateContact.mutateAsync({ id: contact.id, data });
      } else {
        await createContact.mutateAsync(data);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving contact:', error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter full name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Enter email address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="Enter phone number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="designation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Designation</FormLabel>
                <FormControl>
                  <Input placeholder="Enter designation" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contact_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contact type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Legal">Legal</SelectItem>
                    <SelectItem value="Logistics">Logistics</SelectItem>
                    <SelectItem value="Vendor">Vendor</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="linked_brand_ids"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Linked Brands</FormLabel>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {brands.map((brand) => (
                    <div key={brand.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`brand-${brand.id}`}
                        checked={field.value?.includes(brand.id) || false}
                        onCheckedChange={(checked) => {
                          const current = field.value || [];
                          if (checked) {
                            field.onChange([...current, brand.id]);
                          } else {
                            field.onChange(current.filter(id => id !== brand.id));
                          }
                        }}
                      />
                      <label htmlFor={`brand-${brand.id}`} className="text-sm">
                        {brand.name}
                      </label>
                    </div>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="linked_campaign_ids"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Linked Campaigns</FormLabel>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {campaigns.map((campaign) => (
                    <div key={campaign.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`campaign-${campaign.id}`}
                        checked={field.value?.includes(campaign.id) || false}
                        onCheckedChange={(checked) => {
                          const current = field.value || [];
                          if (checked) {
                            field.onChange([...current, campaign.id]);
                          } else {
                            field.onChange(current.filter(id => id !== campaign.id));
                          }
                        }}
                      />
                      <label htmlFor={`campaign-${campaign.id}`} className="text-sm">
                        {campaign.name}
                      </label>
                    </div>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={createContact.isPending || updateContact.isPending}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            {contact ? 'Update Contact' : 'Create Contact'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ContactForm;
